import React, { useState } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { userAdminService } from '@services';
import { GenericForm } from '@components/common/forms';
import { GenericModal } from '@components/common/modals';
import ConfirmActionModal from '@components/admin/ConfirmActionModal';
import Button from '@components/common/Button';
import { isMsalProvider } from '@config/authConfig';
import { ROLES } from '@app-types';
import { CONTACT_STATUS } from '@app-types/user';
import { hasRole } from '@utils/permissionUtils';
import { showToast } from '@utils/toast';
import { getApiErrorMessage } from '@utils/apiError';
import { getActivationMessage } from '@utils/contactUserActivation';
import {
  findUnidentifiedDivisionId,
  getCanLoginFieldState,
  getChangedFields,
  getContactStatusOverrides,
  getRoleChangeUserIdOverrides,
  getUserIdFieldState,
  initialContactStatus,
  isDefaultUserId,
  isEligibleForTemporaryPassword,
  resolveUserFormSubmitError,
  USER_ID_REQUIRED_FOR_ROLES,
  toContactStatusFormValue,
  toEditFormInitialData,
  toEditFormOriginal,
  toNoteField,
  toRoleIds,
  toRoleIdStrings,
  withoutKey
} from '@utils/userForm';
import { buildEditUserFormFields } from './userFormFields';
import { useActivateContactUser } from './useActivateContactUser';
import { useDeactivateContactUser } from './useDeactivateContactUser';
import type { Role } from '@app-types';
import type { AdminUser } from '@app-types/admin';
import type { ContactStatusFormValue, UserFormData, UserFormValue } from '@app-types/userForm';
import styles from './ContactUserEditModal.module.css';
import layout from './UserFormLayout.module.css';

interface ContactUserEditModalProps {
  isOpen: boolean;
  /** The full record (GET /admin/users/:id), with its dependency info */
  user: AdminUser | null;
  roles: Role[];
  divisions: { id: number; name: string }[];
  onClose: () => void;
  /** After a save, a deactivation or an activation, so the caller can refetch */
  onSaved: () => void;
}

/**
 * The Admin's Contact-User edit form, with Deactivate User, Activate User and Send Temporary Password.
 * Opened from the User Management list and from the Contact-User detail page.
 */
const ContactUserEditModal: React.FC<ContactUserEditModalProps> = ({
  isOpen,
  user,
  roles,
  divisions,
  onClose,
  onSaved
}) => {
  const { user: currentUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});
  const [formRoleIds, setFormRoleIds] = useState<string[]>([]);
  const [formContactStatus, setFormContactStatus] = useState<ContactStatusFormValue>(CONTACT_STATUS.ACTIVE);
  const [formOverrides, setFormOverrides] = useState<Partial<UserFormData>>();
  const [isSendPasswordModalOpen, setIsSendPasswordModalOpen] = useState(false);
  const [isSendingTemporaryPassword, setIsSendingTemporaryPassword] = useState(false);

  // Each opening starts from the record as loaded, already on its first render.
  const [loadedUser, setLoadedUser] = useState<AdminUser | null>(null);
  const openUser = isOpen ? user : null;
  if (openUser !== loadedUser) {
    setLoadedUser(openUser);
    if (openUser) {
      setFormRoleIds(openUser.role_ids?.map((id: number) => id.toString()) || []);
      setFormContactStatus(initialContactStatus(openUser));
      setFormOverrides(undefined);
      setExternalErrors({});
    }
  }

  const isEditingSelf = user?.id !== undefined && user.id === currentUser?.id;
  const deactivation = useDeactivateContactUser({
    user,
    onDeactivated: () => {
      onClose();
      onSaved();
    }
  });
  const activation = useActivateContactUser({
    user,
    onActivated: () => {
      onClose();
      onSaved();
    }
  });
  // PreferredName is stored for an Admin only (D-010); for anyone else the field is disabled and not sent.
  const canEditPreferredName = hasRole(currentUser, ROLES.ADMIN);

  const defaultDivisionId = findUnidentifiedDivisionId(divisions);

  const formFields = buildEditUserFormFields({
    roles,
    divisions,
    canEditPreferredName,
    canLogin: getCanLoginFieldState(formRoleIds, formContactStatus),
    userId: getUserIdFieldState(formRoleIds),
    selectedUser: user,
    isEditingSelf,
    isMsalProvider
  });

  // The names of blocked roles the edit would remove (they have active assignments).
  const getBlockedRoleRemovals = (target: AdminUser, data: UserFormData): string[] => {
    const blockedRoles = target.roleDependencies?.blockedRoles ?? [];
    if (blockedRoles.length === 0 || !data.role_ids) return [];
    const newRoleIds = new Set(toRoleIds(data.role_ids));
    return target.role_ids
      .filter((id) => !newRoleIds.has(id))
      .map((roleId) => target.role_names[target.role_ids.indexOf(roleId)])
      .filter((role) => Boolean(role) && blockedRoles.includes(role));
  };

  const handleUpdate = async (data: UserFormData) => {
    if (!user) return;

    const blockedRemovals = getBlockedRoleRemovals(user, data);
    if (blockedRemovals.length > 0) {
      const details = Object.entries(user.roleDependencies?.counts || {})
        .map(([key, count]) => `${key.replace(/_/g, ' ')}: ${count}`)
        .join(', ');
      showToast.error(`Cannot remove role(s): ${blockedRemovals.join(', ')}. Active assignments: ${details}`);
      return;
    }

    setIsSubmitting(true);
    setExternalErrors({});
    try {
      if (toRoleIds(data.role_ids).length > 0 && isDefaultUserId(data.user_id)) {
        setExternalErrors({ user_id: USER_ID_REQUIRED_FOR_ROLES });
        return;
      }
      // Get only the fields that have changed.
      // The Note goes in the same PUT, so it is saved with the change or not at all (ADO-811).
      const changes = {
        ...getChangedFields(toEditFormOriginal(user, defaultDivisionId), data),
        ...toNoteField(data.note)
      };

      // Only proceed if there are actual changes
      if (Object.keys(changes).length === 0) {
        onClose();
        return;
      }

      const saved = await userAdminService.updateUser(user.id, changes);
      onClose();
      onSaved();
      // Setting Contact Status to Active is an activation: the server gives back what the deactivation removed.
      if (saved.restored) showToast.success(getActivationMessage(saved.restored));
    } catch (error) {
      console.error('Update failed', error);
      const { fieldErrors, toastMessage } = resolveUserFormSubmitError(error, formFields, 'Failed to update user');
      setExternalErrors(fieldErrors);
      if (toastMessage) showToast.error(toastMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendTemporaryPassword = async () => {
    if (!user) return;
    setIsSendingTemporaryPassword(true);
    try {
      await userAdminService.sendTemporaryPassword(user.id);
      setIsSendPasswordModalOpen(false);
      showToast.success('Temporary password sent');
    } catch (error) {
      console.error('Send temporary password failed', error);
      showToast.error(getApiErrorMessage(error, 'Failed to send temporary password'));
    } finally {
      setIsSendingTemporaryPassword(false);
    }
  };

  const handleFieldChange = (name: string, value: UserFormValue) => {
    if (name === 'role_ids') {
      const userIdOverride = getRoleChangeUserIdOverrides(name, value, formRoleIds);
      setFormRoleIds(toRoleIdStrings(value));
      if (userIdOverride) setFormOverrides(userIdOverride);
    }
    if (name === 'contact_status') setFormContactStatus(toContactStatusFormValue(value) || CONTACT_STATUS.ACTIVE);
    const overrides = getContactStatusOverrides(name, value);
    if (overrides) {
      setFormOverrides(overrides);
      if (overrides.role_ids !== undefined) setFormRoleIds(toRoleIdStrings(overrides.role_ids));
    }
    // Changing a field clears that field's server error; a role change also rewrites UserID.
    setExternalErrors((prev) => {
      const next = withoutKey(prev, name);
      return name === 'role_ids' ? withoutKey(next, 'user_id') : next;
    });
  };

  return (
    <>
      <GenericModal isOpen={isOpen} onClose={onClose} title="Edit User" size="lg" dataTestId="edit-user-modal">
        {user?.roleDependencies?.hasDependencies && (
          <div className={styles.dependencyWarning} data-test-id="dependency-warning">
            <div className={styles.dependencyWarningTitle}>⚠️ Role Dependencies Detected</div>
            <p className={styles.dependencyWarningText}>
              The following roles cannot be removed due to active assignments:
            </p>
            <ul className={styles.dependencyWarningList}>
              {user.roleDependencies.blockedRoles.map((role: string) => (
                <li key={role}>
                  <strong>{role}</strong>
                </li>
              ))}
            </ul>
            <p className="user-management__dependency-warning-text" style={{ fontSize: '12px', marginTop: '8px' }}>
              Details:{' '}
              {Object.entries(user.roleDependencies.counts)
                .map(([key, count]) => `${key.replace(/_/g, ' ')}: ${count}`)
                .join(', ')}
            </p>
            <p className={styles.dependencyWarningText}>
              You can still add new roles or remove other roles without dependencies.
            </p>
          </div>
        )}
        <div className={layout.modalContent}>
          <GenericForm
            fields={formFields}
            initialData={toEditFormInitialData(user, defaultDivisionId)}
            onSubmit={handleUpdate}
            onCancel={onClose}
            onFieldChange={handleFieldChange}
            externalValues={formOverrides}
            externalErrors={externalErrors}
            isSubmitting={isSubmitting}
          >
            {user?.is_active && (
              <div className={layout.statusAction}>
                <Button
                  type="button"
                  variant="danger"
                  dataTestId="deactivate-user-button"
                  disabled={isSubmitting || deactivation.isDisabled}
                  title={deactivation.tooltip}
                  onClick={deactivation.openConfirm}
                >
                  Deactivate User
                </Button>
              </div>
            )}
            {user && !user.is_active && (
              <div className={layout.statusAction}>
                <Button
                  type="button"
                  variant="primary"
                  dataTestId="activate-user-button"
                  disabled={isSubmitting || activation.isDisabled}
                  title={activation.tooltip}
                  onClick={() => activation.openConfirm()}
                >
                  Activate User
                </Button>
              </div>
            )}
            {user && isEligibleForTemporaryPassword(user) && (
              <div className={layout.formExtraActions}>
                <Button
                  type="button"
                  variant="secondary"
                  dataTestId="send-temporary-password-button"
                  disabled={isSubmitting || isSendingTemporaryPassword}
                  onClick={() => setIsSendPasswordModalOpen(true)}
                >
                  Send Temporary Password
                </Button>
              </div>
            )}
          </GenericForm>
        </div>
      </GenericModal>

      {deactivation.confirmModal}
      {activation.confirmModal}

      <ConfirmActionModal
        isOpen={isSendPasswordModalOpen}
        onClose={() => setIsSendPasswordModalOpen(false)}
        onConfirm={handleSendTemporaryPassword}
        title="Send Temporary Password"
        message={`This will generate a new temporary password and email it to ${user?.email}. The previous password will stop working.`}
        isSubmitting={isSendingTemporaryPassword}
        actionType="warning"
      />
    </>
  );
};

export default ContactUserEditModal;
